import React, { useState, useEffect } from 'react';
import {
	Card,
	CardContent,
	Typography,
	Button,
	CircularProgress,
	Alert,
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	IconButton,
	Tooltip,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TablePagination,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SaveIcon from '@mui/icons-material/Save';
import { Title } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import {
	MCPAnalysisWithUser,
	MetaAnalysisResult,
	listAnalyses,
	metaAnalyze,
	saveMetaAnalysis,
} from '../../Services/quantiAI';

const getScoreColor = (score: number): 'success' | 'warning' | 'error' | 'default' => {
	if (score >= 7) return 'success';
	if (score >= 4) return 'warning';
	return 'error';
};

const formatDate = (dateStr: string) => {
	const date = new Date(dateStr);
	return date.toLocaleString('fr-FR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

export const AIAnalysesList = () => {
	const [analyses, setAnalyses] = useState<MCPAnalysisWithUser[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const [scopeFilter, setScopeFilter] = useState<string>('session');
	const [sinceFilter, setSinceFilter] = useState<string>('');
	const [untilFilter, setUntilFilter] = useState<string>('');

	// Meta-analysis state
	const [metaOpen, setMetaOpen] = useState(false);
	const [metaLoading, setMetaLoading] = useState(false);
	const [metaResult, setMetaResult] = useState<MetaAnalysisResult | null>(null);
	const [metaError, setMetaError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);

	const navigate = useNavigate();

	const loadData = async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await listAnalyses({
				limit: rowsPerPage,
				offset: page * rowsPerPage,
				scope: scopeFilter || undefined,
				status: 'completed',
				since: sinceFilter ? new Date(sinceFilter).toISOString() : undefined,
				until: untilFilter ? new Date(untilFilter).toISOString() : undefined,
			});
			setAnalyses(res.analyses || []);
			setTotal(res.total);
		} catch (err: any) {
			setError(err.message || 'Erreur lors du chargement');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadData();
	}, [page, rowsPerPage, scopeFilter, sinceFilter, untilFilter]);

	const handleMetaAnalyze = async () => {
		setMetaLoading(true);
		setMetaError(null);
		setMetaResult(null);
		setSaved(false);
		setMetaOpen(true);
		try {
			const result = await metaAnalyze({
				scope: scopeFilter || undefined,
				since: sinceFilter ? new Date(sinceFilter).toISOString() : undefined,
				until: untilFilter ? new Date(untilFilter).toISOString() : undefined,
				limit: 50,
			});
			setMetaResult(result);
		} catch (err: any) {
			setMetaError(err.message || 'Erreur lors de la méta-analyse');
		} finally {
			setMetaLoading(false);
		}
	};

	const handleSaveMeta = async () => {
		if (!metaResult) return;
		setSaving(true);
		try {
			await saveMetaAnalysis(metaResult.content, metaResult.model, metaResult.total_tokens);
			setSaved(true);
		} catch (err: any) {
			setMetaError(err.message || 'Erreur lors de la sauvegarde');
		} finally {
			setSaving(false);
		}
	};

	const handleCloseModal = () => {
		setMetaOpen(false);
		setMetaResult(null);
		setMetaError(null);
		setSaved(false);
		if (saved) loadData();
	};

	return (
		<Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
			<Card>
				<Title title="Analyses IA" />
				<CardContent>
					{/* Header */}
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
						<Typography variant="h5">
							Analyses IA ({total})
						</Typography>
						<Box sx={{ display: 'flex', gap: 1 }}>
							<Button
								variant="contained"
								startIcon={metaLoading ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
								onClick={handleMetaAnalyze}
								disabled={metaLoading || total === 0}
								size="small"
							>
								Synthèse globale
							</Button>
							<IconButton onClick={loadData} disabled={loading}>
								<RefreshIcon />
							</IconButton>
						</Box>
					</Box>

					{/* Filtres */}
					<Paper variant="outlined" sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'flex-end' }}>
						<FormControl size="small" sx={{ width: 150, flexShrink: 0 }}>
							<InputLabel>Scope</InputLabel>
							<Select
								value={scopeFilter}
								label="Scope"
								onChange={(e) => { setScopeFilter(e.target.value); setPage(0); }}
							>
								<MenuItem value="">Tous</MenuItem>
								<MenuItem value="session">Session</MenuItem>
								<MenuItem value="user">Utilisateur</MenuItem>
								<MenuItem value="project">Projet</MenuItem>
								<MenuItem value="global">Global</MenuItem>
							</Select>
						</FormControl>
						<TextField
							size="small"
							label="Depuis"
							type="date"
							value={sinceFilter}
							onChange={(e) => { setSinceFilter(e.target.value); setPage(0); }}
							InputLabelProps={{ shrink: true }}
							sx={{ width: 180, flexShrink: 0 }}
						/>
						<TextField
							size="small"
							label="Jusqu'à"
							type="date"
							value={untilFilter}
							onChange={(e) => { setUntilFilter(e.target.value); setPage(0); }}
							InputLabelProps={{ shrink: true }}
							sx={{ width: 180, flexShrink: 0 }}
						/>
						{(sinceFilter || untilFilter) && (
							<Button
								size="small"
								variant="outlined"
								onClick={() => { setSinceFilter(''); setUntilFilter(''); setPage(0); }}
								sx={{ flexShrink: 0 }}
							>
								Réinitialiser
							</Button>
						)}
					</Paper>

					{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

					{loading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
							<CircularProgress />
						</Box>
					) : (
						<>
							<TableContainer component={Paper} variant="outlined">
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Score</TableCell>
											<TableCell>Utilisateur</TableCell>
											<TableCell>Résumé</TableCell>
											<TableCell>Recos</TableCell>
											<TableCell>Modèle</TableCell>
											<TableCell>Tokens</TableCell>
											<TableCell>Date</TableCell>
											<TableCell align="center">Actions</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{analyses.length === 0 ? (
											<TableRow>
												<TableCell colSpan={8} align="center">
													<Typography color="text.secondary" sx={{ py: 2 }}>Aucune analyse trouvée</Typography>
												</TableCell>
											</TableRow>
										) : (
											analyses.map((a) => (
												<TableRow key={a.id} hover>
													<TableCell>
														{a.result?.score != null ? (
															<Chip
																label={`${a.result.score}/10`}
																color={getScoreColor(a.result.score)}
																size="small"
															/>
														) : (
															<Chip label="—" size="small" />
														)}
													</TableCell>
													<TableCell>
														<Typography variant="body2">
															{a.user_email || (a.user_id ? `User #${a.user_id}` : '—')}
														</Typography>
													</TableCell>
													<TableCell sx={{ maxWidth: 300 }}>
														<Typography variant="body2" noWrap title={a.result?.summary}>
															{a.result?.summary || '—'}
														</Typography>
													</TableCell>
													<TableCell>
														{a.result?.recommendations?.length ? (
															<Chip
																label={a.result.recommendations.length}
																size="small"
																color="info"
																variant="outlined"
															/>
														) : '—'}
													</TableCell>
													<TableCell>
														<Typography variant="body2" color="text.secondary">
															{a.model?.replace('claude-', '').replace('-20250514', '') || '—'}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography variant="body2" color="text.secondary">
															{a.total_tokens || '—'}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography variant="body2" color="text.secondary">
															{formatDate(a.created_at)}
														</Typography>
													</TableCell>
													<TableCell align="center">
														{a.session_id && (
															<Tooltip title="Voir la session">
																<IconButton
																	size="small"
																	onClick={() => navigate(`/ai-conversations/${a.session_id}`)}
																>
																	<VisibilityIcon fontSize="small" />
																</IconButton>
															</Tooltip>
														)}
													</TableCell>
												</TableRow>
											))
										)}
									</TableBody>
								</Table>
							</TableContainer>
							<TablePagination
								component="div"
								count={total}
								page={page}
								onPageChange={(_, p) => setPage(p)}
								rowsPerPage={rowsPerPage}
								onRowsPerPageChange={(e) => {
									setRowsPerPage(parseInt(e.target.value, 10));
									setPage(0);
								}}
								rowsPerPageOptions={[10, 25, 50, 100]}
								labelRowsPerPage="Par page"
							/>
						</>
					)}
				</CardContent>
			</Card>

			{/* Modal méta-analyse */}
			<Dialog
				open={metaOpen}
				onClose={handleCloseModal}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>
					Synthèse globale des analyses
				</DialogTitle>
				<DialogContent dividers>
					{metaLoading && (
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
							<CircularProgress />
							<Typography color="text.secondary">Analyse en cours...</Typography>
						</Box>
					)}

					{metaError && <Alert severity="error" sx={{ mb: 2 }}>{metaError}</Alert>}

					{metaResult && (
						<Box>
							<Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
								<Chip label={metaResult.model?.replace('claude-', '').replace('-20250514', '')} size="small" />
								<Chip label={`${metaResult.total_tokens} tokens`} size="small" variant="outlined" />
							</Box>
							<Divider sx={{ mb: 2 }} />
							<Paper
								sx={{
									p: 2,
									bgcolor: 'grey.50',
									whiteSpace: 'pre-wrap',
									wordBreak: 'break-word',
									fontFamily: 'system-ui',
									fontSize: '0.875rem',
									lineHeight: 1.6,
									maxHeight: '60vh',
									overflow: 'auto',
								}}
							>
								{metaResult.content}
							</Paper>
						</Box>
					)}

					{saved && (
						<Alert severity="success" sx={{ mt: 2 }}>
							Analyse globale enregistrée avec succès
						</Alert>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseModal}>Fermer</Button>
					{metaResult && !saved && (
						<Button
							variant="contained"
							startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
							onClick={handleSaveMeta}
							disabled={saving}
						>
							Enregistrer
						</Button>
					)}
				</DialogActions>
			</Dialog>
		</Box>
	);
};
